import { motion } from "framer-motion";
import { MEDIA } from "@/lib/content";

const pillars = [
  {
    label: "EV charging",
    desc: "Turnkey design, supply, install & ops for AC/DC chargers.",
  },
  {
    label: "Solar EPC",
    desc: "Rooftop and ground-mount solar plants, grid-tied or hybrid.",
  },
  {
    label: "Hybrid inverters",
    desc: "Smart inverters that balance solar, storage and grid seamlessly.",
  },
  {
    label: "Power Purchase",
    desc: "Zero-capex PPA models so you pay only for the power you use.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-pad bg-secondary/40"
    >
      <div className="container-outer">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5 relative"
          >
            <div className="rounded-lg overflow-hidden border border-border">
              <img
                src={MEDIA.about}
                alt="Clean tech in action"
                className="w-full h-[420px] md:h-[520px] object-cover"
              />
            </div>
            <div
              className="absolute -bottom-6 -right-6 hidden md:block bg-accent text-accent-foreground rounded-lg px-6 py-5 max-w-[220px]"
              data-testid="about-badge"
            >
              <p className="font-heading text-4xl font-medium leading-none">
                2+
              </p>
              <p className="text-sm mt-2 opacity-90">
                years building renewable infrastructure
              </p>
            </div>
          </motion.div>

          {/* Copy + pillars */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-7"
          >
            <p className="overline mb-4">About us</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium text-primary heading-tight">
              Engineers, not salespeople.
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              We are a team of power engineers, EV specialists and project
              financiers obsessed with one thing: making clean energy the
              obvious choice on a spreadsheet. Every project we deliver is
              designed to pay for itself — often in under five years.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 mt-10">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`pillar-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="border-t border-border pt-5"
                >
                  <p className="font-heading text-xl md:text-2xl font-medium text-primary">
                    {p.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {p.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
